import { useState } from 'react'
import {
  Image,
  Modal,
  ModalClose,
  ModalContent,
  Button,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useCertificateSlug } from 'pages/certificates/common'
import {
  TrackOnMount,
  TrackingAttributes,
  useFireEvent,
  TrackClick,
} from 'pages/certificates/tracking'
import { DownloadSyllabusForm } from './DownloadSyllabusForm'
import classes from './DownloadSyllabus.module.scss'

export interface SyllabusData {
  syllabusPreviewImageUrl?: string
  title?: string
  syllabusPdfUrl: string
}

interface DownloadSyllabusModalProps {
  show: boolean
  onClose: () => void
  syllabusData: SyllabusData
}

export const DownloadSyllabusModal = ({
  show,
  onClose,
  syllabusData: { syllabusPreviewImageUrl = '', title = '', syllabusPdfUrl },
}: DownloadSyllabusModalProps) => {
  const { certSlug } = useCertificateSlug()
  const { t } = useTranslation('@mc/certificates')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const fireEvent = useFireEvent()

  const handleSubmit = ({ email }: { email: string }) => {
    setIsSubmitted(true)
    fireEvent('syllabusDownloaded', {
      email,
      campaign: certSlug,
      syllabus_link: syllabusPdfUrl,
      location: 'syllabus',
    })
  }

  const handleOnClose = () => {
    fireEvent('syllabusModalClicked', {
      action: 'cancel',
      resource_link: syllabusPdfUrl,
      resource_title: title,
    })
    onClose()
  }

  return (
    <Modal
      appendToBody={false}
      show={show}
      onClose={handleOnClose}
      size='large'
      noInitialFocus
      canClickOutsideToClose={false}
      backdrop='dark'
      containerClassName={classes.modal}
    >
      <TrackingAttributes resource_link={syllabusPdfUrl} resource_title={title}>
        <TrackOnMount event='syllabusModalViewed' />
        <ModalContent className='c-bg-base-300 mc-corners--md'>
          <ModalClose />
          <div className='d-flex c-p-7 mc-gap-sm-9'>
            {syllabusPreviewImageUrl && (
              <div className={`${classes.image} d-none d-md-block`}>
                <Image
                  className='mc-corners--sm'
                  src={syllabusPreviewImageUrl}
                  alt='Certificate Syllabus'
                  height={264}
                  width={211}
                />
              </div>
            )}
            <div className='d-flex flex-column justify-content-center mc-gap-7'>
              {isSubmitted ? (
                <>
                  <div>
                    <h2 className='mc-text-h2 mc-mb-4'>
                      {t('downloadSyllabus.thankYou')}
                    </h2>
                    <p className='c-text-color--tint-3'>
                      {t('downloadSyllabus.syllabusSent')}
                    </p>
                  </div>
                  <TrackClick event='syllabusModalClicked'>
                    <Button
                      className={classes.button}
                      render={
                        <a
                          href={syllabusPdfUrl}
                          target='_blank'
                          rel='noreferrer'
                        />
                      }
                    >
                      {t('downloadSyllabus.viewSyllabus')}
                    </Button>
                  </TrackClick>
                </>
              ) : (
                <>
                  <div>
                    <h2 className='mc-text-h2 mc-mb-4'>
                      {t('downloadSyllabus.title')}
                    </h2>
                    <p className='c-text-color--tint-3'>
                      {t('downloadSyllabus.description', {
                        certificateName: title,
                      })}
                    </p>
                  </div>
                  <DownloadSyllabusForm onSubmit={handleSubmit} />
                </>
              )}
            </div>
          </div>
        </ModalContent>
      </TrackingAttributes>
    </Modal>
  )
}
